import React, { useRef, useEffect } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"
import { orderBy } from "lodash"

import { applyCategoryOrder } from "../categoryOrder"

// Utils
import { useCleanChart } from "../utils/hooks/useCleanChart"
import { filterMenuItems, getNumberFormat, transformeData } from "../utils/global"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  resolveMappedColor,
  resolveCategoryColor,
} from "../utils/utils"
import type { ColorMapperRow } from "../utils/utils"
import {
  convertDataPyramide,
  setupPyramideChart,
  dataSortByYOrder,
  makeDataPyramide,
  findTopVisibleStackIndex,
} from "./utils"

// Design tokens
import { colors } from "Shared/tokens"

// Types
interface BarChartProps {
  columnCornerRadiusBottomRight?: number
  columnCornerRadiusBottomLeft?: number
  columnCornerRadiusTopRight?: number
  columnCornerRadiusTopLeft?: number
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  disabledTooltipAxesX?: boolean
  disabledTooltipAxesY?: boolean
  showSpaceForThousand?: boolean
  columnsWidthPercent?: number
  rotationLabelAxesX?: number
  fontSizeLabelAxesX?: number
  fontSizeLabelAxesY?: number
  cellStartLocation?: number
  positionScrollBar?: "top" | "bottom"
  disabledGridAxesX?: boolean
  disabledGridAxesY?: boolean
  textTooltipBefore?: string[]
  enableExportMenu?: boolean
  decimalSeparator?: string
  textTooltipAfter?: string[]
  visibilityLegend?: boolean
  displayTypeLabel?: "truncate" | "wrap" | "default"
  showAsPercentage?: boolean
  cellEndLocation?: number
  enableAxisBreak?: boolean
  legendPosition?: "top" | "bottom" | "left" | "right"
  visibilityZoom?: boolean
  widthGridValue?: number
  useMinMaxYAxis?: boolean
  extraMinAxesY?: number | string
  extraMaxAxesY?: number
  axisStartZero?: boolean
  /** Graduations ENTIÈRES sur l'axe de valeurs (`maxPrecision: 0`). Voir la note dans `setupChart`. */
  integerValueAxis?: boolean
  colorSetTheme?: string[]
  colorMapper?: ColorMapperRow[]
  numberDecimal?: number
  startLocation?: number
  categoryData: string | string[]
  widgetTitle?: string
  endLocation?: number
  axisBreaks?: Array<{ startValue?: number; endValue?: number }>
  dataChart: any[]
  valueData: string[]
  dataNames: string[]
  titleAxeX?: string
  titleAxeY?: string
  minAxesY?: number
  maxAxesY?: number
  minYAxis?: number
  maxYAxis?: number
  idChart?: string
  width?: string
  height?: string
  textToAddOnValueAxis?: string
  valuePercentUnderWhichHideLabel?: number
  hideLabelIfPercentTooSmall?: boolean
  forceDisplayPercentSymbol?: boolean
  positionAxeYLabelColumn?: number
  listOfAxisRangeObject?: any[]
  locationYLabelColumn?: number
  locationXLabelColumn?: number
  rotationLabelColumn?: number
  truncateLabelColumn?: boolean
  fontSizeLabelColumn?: number
  markerLegendHeight?: number
  markerLegendWidth?: number
  enableAxisRange?: boolean
  isStackedBar100?: boolean
  showStackTotal?: boolean
  stackTotalFontSize?: number
  stackTotalColor?: string
  barOrientation?: "vertical" | "horizontal"
  hideAxeXLabel?: boolean
  hideAxeYLabel?: boolean
  disableLabel?: boolean
  /**
   * Où poser la valeur d'une barre : DEDANS (défaut historique) ou à côté.
   *
   * Dedans, l'étiquette est blanche et centrée — illisible dès que la barre est courte, et c'est
   * le reproche du client CRC le 2026-09-22. Dehors, elle se pose au bout de la barre et reprend
   * la couleur de texte du thème.
   *
   * ⚠️ Le défaut reste `"inside"` : ce composant sert TOUS les histogrammes de la plateforme, et
   * déplacer les étiquettes de tout le monde n'a été demandé par personne.
   */
  labelColumnPosition?: "inside" | "outside"
  typeBarChart?: "histogram" | "stackedBar"
  tooltipActivation?: boolean
  tooltipValueType?: "text_and_value" | "value" | "text"
  widgetGroupBy?: any[]
  /**
   * Ordre EXPLICITE des catégories de l'axe. Les valeurs hors liste sont conservées et placées en
   * fin — voir `chartsLibrary/categoryOrder`. Vide/absent = ordre de la donnée, inchangé.
   */
  categoryOrder?: unknown[]
  /** Conserve une catégorie de `categoryOrder` sans donnée, à 0, au lieu de l'omettre de l'axe. */
  keepEmptyCategories?: boolean
  /** @deprecated Alias historique de `categoryOrder`, jamais câblé jusqu'ici. */
  yAxisOrder?: any[]
  labelPyramide1?: string
  labelPyramide2?: string
  colorPyramide1?: string
  colorPyramide2?: string
  fieldsData?: any
  requestLoad?: any
  withColumnsOrder?: boolean
  onSegmentClick?: (payload: { categoryValue: string; seriesIndex: number }) => void
}

const BarChart: React.FC<BarChartProps> = ({
  columnCornerRadiusBottomRight = 4,
  columnCornerRadiusBottomLeft = 4,
  columnCornerRadiusTopRight = 4,
  columnCornerRadiusTopLeft = 4,
  displayTypeLegendLabels = "truncate",
  disabledTooltipAxesX = false,
  disabledTooltipAxesY = true,
  showSpaceForThousand = true,
  columnsWidthPercent = 100,
  rotationLabelAxesX = -45,
  fontSizeLabelAxesX = 12,
  fontSizeLabelAxesY = 12,
  cellStartLocation = 0,
  positionScrollBar = "bottom",
  disabledGridAxesX = false,
  disabledGridAxesY = false,
  textTooltipBefore = [],
  enableExportMenu = false,
  decimalSeparator = ",",
  textTooltipAfter = [],
  visibilityLegend = true,
  displayTypeLabel = "default",
  showAsPercentage = false,
  cellEndLocation = 1,
  enableAxisBreak = false,
  legendPosition = "right",
  visibilityZoom = false,
  widthGridValue = 40,
  useMinMaxYAxis = false,
  extraMinAxesY = 0.1,
  extraMaxAxesY = 0.1,
  axisStartZero = false,
  integerValueAxis = false,
  colorSetTheme = ["#677CC1", "#53AC86", "#FFC552"],
  colorMapper = [],
  numberDecimal = 2,
  startLocation = 0,
  categoryData,
  widgetTitle = "barchart",
  endLocation = 1,
  axisBreaks = [{ startValue: 90, endValue: 100 }],
  dataChart = [],
  valueData = [],
  dataNames = [],
  titleAxeX = "",
  titleAxeY = "",
  minAxesY,
  maxAxesY,
  minYAxis = 0,
  maxYAxis,
  idChart,
  width = "100%",
  height = "100%",
  textToAddOnValueAxis = "",
  valuePercentUnderWhichHideLabel = 120,
  hideLabelIfPercentTooSmall = false,
  forceDisplayPercentSymbol = false,
  positionAxeYLabelColumn = 0,
  listOfAxisRangeObject = [{}],
  locationYLabelColumn,
  locationXLabelColumn,
  rotationLabelColumn = 0,
  truncateLabelColumn = false,
  fontSizeLabelColumn = 15,
  markerLegendHeight = 20,
  markerLegendWidth = 20,
  enableAxisRange = false,
  isStackedBar100 = false,
  showStackTotal = false,
  stackTotalFontSize = 13,
  stackTotalColor = colors.textPrimary,
  barOrientation = "vertical",
  hideAxeXLabel = false,
  hideAxeYLabel = false,
  disableLabel = false,
  labelColumnPosition = "inside",
  typeBarChart = "histogram",
  tooltipActivation = true,
  tooltipValueType = "",
  widgetGroupBy = [],
  categoryOrder,
  keepEmptyCategories = false,
  yAxisOrder = [],
  fieldsData,
  requestLoad,
  withColumnsOrder = false,
  onSegmentClick,
}) => {
  const ID = useRef(idChart || `BarChart${Math.random()}`).current
  const chartRef = useRef<am5xy.XYChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const { cleanChart } = useCleanChart()

  // Le total ne s'affiche que sur l'empile en valeurs absolues : en mode 100 % il vaut
  // toujours 100 %, ce qui n'apprend rien de plus que la colonne elle-meme.
  const displayStackTotal = showStackTotal && typeBarChart === "stackedBar" && !isStackedBar100

  const shouldFilter = Number(extraMinAxesY) === 0.1
  const filteredDataChart = shouldFilter
    ? dataChart?.filter(item =>
        valueData.some(key => {
          const num = Number(item?.[key])
          return Number.isFinite(num) && num > 0
        })
      )
    : dataChart

  const dataTransformed = transformeData(requestLoad, fieldsData, filteredDataChart)

  /**
   * Ordre des catégories de l'axe, en un seul endroit — les deux chemins de rendu (construction
   * initiale et reconstruction sur changement de props) doivent trier IDENTIQUEMENT, sans quoi le
   * graphique change d'ordre au premier re-rendu.
   *
   * Précédence : un ordre explicite gagne sur `withColumnsOrder` (tri par valeur décroissante). Les
   * deux sont contradictoires par nature, et c'est l'ordre explicite qui est demandé par le métier.
   * Sans ordre explicite, le comportement est **exactement** celui d'avant — c'est ce qui garantit
   * qu'aucun autre graphique de l'application ne bouge.
   */
  const trierCategories = (data: any[], categoryField: string) => {
    const ordre = categoryOrder ?? (yAxisOrder?.length ? yAxisOrder : undefined)
    if (ordre?.length) {
      return applyCategoryOrder(data, categoryField, ordre, { padMissing: keepEmptyCategories })
    }
    return withColumnsOrder ? orderBy(data, [valueData?.[0]], ["desc"]) : data
  }

  const prepareChartData = (rawData: any[]) => {
    let cleaned = rawData.map(r => {
      const copy = { ...r }
      valueData.forEach(k => {
        if (copy[k] === 0) copy[k] = undefined
      })
      return copy
    })

    let withCombined = cleaned.map(item => {
      if (Array.isArray(categoryData)) {
        const combined = categoryData
          .map(key => {
            const val = item[key]
            return val != null ? String(val) : ""
          })
          .join(" / ")
        return { ...item, combinedCategory: combined }
      }
      return { ...item }
    })

    if (Array.isArray(categoryData)) {
      withCombined = withCombined.map(item => {
        const cc = item.combinedCategory.trim()
        return {
          ...item,
          combinedCategory: cc !== "" ? cc : "Sans valeur",
        }
      })
    }

    return withCombined
  }

  const initChart = () => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    // Set themes
    root.setThemes([am5themes_Animated.new(root)])

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
      })
    )

    chartRef.current = chart

    // Prepare data
    let processedData = prepareChartData(filteredDataChart)

    setupChart(chart, root)

    const categoryField = Array.isArray(categoryData) ? "combinedCategory" : categoryData
    const dataSort = trierCategories(processedData, categoryField)

    // Build axes, series, and legend
    rebuildChartStructure(chart, root, categoryField, dataSort)
  }

  const setupChart = (chart: am5xy.XYChart, root: am5.Root) => {
    // Language configuration
    if (decimalSeparator === ",") {
      root.numberFormatter.set("numberFormat", "#,###.##")
    }

    // Number formatting
    root.numberFormatter.setAll({
      numberFormat: getNumberFormat(numberDecimal, showSpaceForThousand),
      numericFields: ["value"],
    })

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Scrollbar
    if (visibilityZoom) {
      const scrollbarX = am5xy.XYChartScrollbar.new(root, {
        orientation: "horizontal",
      })
      chart.set("scrollbarX", scrollbarX)
    }
  }

  const rebuildChartStructure = (
    chart: am5xy.XYChart,
    root: am5.Root,
    categoryField: string,
    processedData: any[]
  ) => {
    let categoryAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>
    let valueAxis: am5xy.ValueAxis<am5xy.AxisRenderer>

    if (barOrientation === "vertical") {
      // Category axis (X)
      categoryAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: categoryField,
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: widthGridValue,
            cellStartLocation: cellStartLocation,
            cellEndLocation: cellEndLocation,
          }),
          tooltip: am5.Tooltip.new(root, {}),
        })
      )

      categoryAxis.get("renderer").labels.template.setAll({
        rotation: rotationLabelAxesX,
        fontSize: fontSizeLabelAxesX,
        centerX: am5.p50,
        centerY: am5.p50,
      })

      // Category axis title
      if (titleAxeX && !hideAxeXLabel) {
        categoryAxis.children.push(
          am5.Label.new(root, {
            text: titleAxeX,
            x: am5.p50,
            centerX: am5.p50,
          })
        )
      }

      if (displayTypeLabel === "truncate") {
        categoryAxis.get("renderer").labels.template.setAll({
          oversizedBehavior: "truncate",
          maxWidth: 100,
          tooltipText: "{category}",
        })
      } else if (displayTypeLabel === "wrap") {
        categoryAxis.get("renderer").labels.template.setAll({
          oversizedBehavior: "wrap",
          maxWidth: 100,
          tooltipText: "{category}",
        })
      }

      categoryAxis.get("renderer").grid.template.set("visible", !disabledGridAxesX)

      // Value axis (Y)
      valueAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: widthGridValue,
          }),
        })
      )
    } else {
      // Horizontal orientation
      categoryAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: categoryField,
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: widthGridValue,
            cellStartLocation: cellStartLocation,
            cellEndLocation: cellEndLocation,
            inversed: true,
          }),
        })
      )

      categoryAxis.get("renderer").labels.template.setAll({
        fontSize: fontSizeLabelAxesX,
        centerX: am5.p100,
        centerY: am5.p50,
      })

      // Category axis title (for horizontal bars, this is the Y axis)
      if (titleAxeX && !hideAxeXLabel) {
        categoryAxis.children.unshift(
          am5.Label.new(root, {
            text: titleAxeX,
            rotation: -90,
            y: am5.p50,
            centerX: am5.p50,
          })
        )
      }

      if (displayTypeLabel === "truncate") {
        categoryAxis.get("renderer").labels.template.setAll({
          oversizedBehavior: "truncate",
          maxWidth: 100,
          tooltipText: "{category}",
        })
      } else if (displayTypeLabel === "wrap") {
        categoryAxis.get("renderer").labels.template.setAll({
          oversizedBehavior: "wrap",
          maxWidth: 100,
          maxHeight: 60,
          tooltipText: "{category}",
        })
      }

      categoryAxis.get("renderer").grid.template.set("visible", !disabledGridAxesX)

      valueAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: widthGridValue,
          }),
        })
      )
    }

    // Configure value axis
    let axisTitle = titleAxeY
    if (textToAddOnValueAxis && textToAddOnValueAxis !== "") {
      axisTitle = `${titleAxeY} ${textToAddOnValueAxis}`
    }

    valueAxis.get("renderer").grid.template.set("visible", !disabledGridAxesY)

    // Value axis title
    if (!hideAxeYLabel) {
      if (barOrientation === "vertical") {
        valueAxis.children.unshift(
          am5.Label.new(root, {
            text: axisTitle,
            rotation: -90,
            y: am5.p50,
            centerX: am5.p50,
          })
        )
      } else {
        valueAxis.children.push(
          am5.Label.new(root, {
            text: axisTitle,
            x: am5.p50,
            centerX: am5.p50,
          })
        )
      }
    }

    // Graduations entieres. Sans ce reglage, amCharts choisit librement le pas de l'axe : sur de
    // petites valeurs entieres il tombe sur un pas fractionnaire (0.5), et le formateur du root
    // arrondit les libelles a l'affichage -> deux graduations voisines s'ecrivent a l'identique
    // (« 3.0 » deux fois pour 2.5 et 3.0). `maxPrecision: 0` fait choisir un pas entier a l'axe
    // lui-meme, ce qu'aucun formatage ne peut rattraper apres coup.
    if (integerValueAxis) {
      valueAxis.set("maxPrecision", 0)
    }

    if (useMinMaxYAxis && minYAxis !== undefined && maxYAxis !== undefined && minYAxis < maxYAxis) {
      valueAxis.set("min", minYAxis)
      valueAxis.set("max", maxYAxis)
      valueAxis.set("strictMinMax", true)
    } else if (axisStartZero) {
      valueAxis.set("min", 0)
      valueAxis.set("extraMin", 0)
      valueAxis.set("extraMax", Number(extraMaxAxesY))
    } else {
      valueAxis.set("extraMin", Number(extraMinAxesY))
      valueAxis.set("extraMax", Number(extraMaxAxesY))
    }

    if (typeBarChart === "stackedBar") {
      valueAxis.set("min", 0)
      valueAxis.set("extraMin", 0)
      if (isStackedBar100) {
        valueAxis.set("extraMax", 0)
        valueAxis.set("max", 100)
        valueAxis.set("calculateTotals", true)
      }
      if (displayStackTotal) {
        // amCharts ne renseigne `valueYTotal` / `valueXTotal` sur les points de donnees
        // que si le calcul des totaux est demande explicitement sur l'axe de valeurs.
        valueAxis.set("calculateTotals", true)
      }
      valueAxis.set("strictMinMax", false)
    }

    // Axis breaks
    if (enableAxisBreak && axisBreaks) {
      axisBreaks.forEach((axisBreak: any) => {
        const startValue = axisBreak.startValue || 90
        const endValue = axisBreak.endValue || 100

        const breakRange = valueAxis.createAxisRange(
          valueAxis.makeDataItem({
            value: startValue,
            endValue: endValue,
          })
        )

        breakRange.get("axisFill")?.setAll({
          fill: am5.color(0xcccccc),
          fillOpacity: 0.2,
          visible: true,
        })

        breakRange.get("grid")?.setAll({
          strokeDasharray: [2, 2],
          strokeOpacity: 0.5,
        })
      })
    }

    // Axis ranges
    if (enableAxisRange && listOfAxisRangeObject) {
      listOfAxisRangeObject.forEach((rangeObj: any) => {
        const axisRange = valueAxis.createAxisRange(
          valueAxis.makeDataItem({
            value: rangeObj.value,
          })
        )

        axisRange.get("grid")?.setAll({
          stroke: am5.color(rangeObj.strokeColor),
          strokeWidth: rangeObj.strokeWidth,
          strokeOpacity: rangeObj.strokeOpacity,
        })

        axisRange.get("label")?.setAll({
          text: rangeObj.textLabel,
          fill: am5.color(rangeObj.colorLabel),
          inside: rangeObj.inside,
        })
      })
    }

    // Set data
    categoryAxis.data.setAll(processedData)

    // Add series
    addSerieToChart(chart, root, categoryField, processedData)

    // Legend (created after series so it can connect to them)
    const legend = createLegend(chart, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      markerLegendWidth,
      markerLegendHeight,
    })

    if (legend) {
      legend.data.setAll(chart.series.values)
    }
  }

  /**
   * Le total d'une pile ne doit apparaitre qu'une fois, au sommet de la colonne. On pose
   * donc le bullet sur TOUTES les series et on l'efface partout sauf sur la serie visible
   * la plus haute : le poser sur la seule derniere serie ferait disparaitre le total des
   * que l'utilisateur masque cette serie depuis la legende.
   */
  const isTopVisibleStackSeries = (
    chart: am5xy.XYChart,
    seriesIndex: number,
    dataItem?: { dataContext: unknown }
  ): boolean => {
    const topmostIndex = findTopVisibleStackIndex(
      dataItem?.dataContext as Record<string, unknown> | undefined,
      valueData,
      j => {
        const candidate = chart.series.getIndex(j)
        return !candidate || candidate.isHidden()
      }
    )
    return topmostIndex === seriesIndex
  }

  /**
   * Couleur par valeur de CATEGORIE (l'axe des abscisses en vertical).
   *
   * Un histogramme mono-serie peint aujourd'hui toutes ses colonnes de la couleur de la serie :
   * c'est la categorie qui porte le sens, pas la serie. L'adapter ne rend une couleur que si une
   * regle correspond, donc un widget sans regle garde exactement son rendu.
   */
  const applyCategoryColorMapper = (
    series: am5xy.ColumnSeries,
    orientation: "vertical" | "horizontal"
  ) => {
    if (!colorMapper.length) return

    // ⚠️ NE PAS repasser par `series.columns.template.adapters.add("fill", …)`. C'est la recette
    // amCharts habituelle, mais elle ne peut pas lire la catégorie : `_createGraphics` crée la
    // colonne PUIS lui rattache son point de données (`makeGraphics` puis `_setDataItem`). Au
    // moment où l'adapter lit `target.dataItem`, il n'y a rien — la fonction s'abstient et rend la
    // couleur d'origine. Aucune erreur, aucune couleur : le mode d'échec le plus silencieux
    // possible, et celui qui a été constaté à l'écran le 2026-08-19 sur un histogramme simple
    // alors que le chemin « nom de série » fonctionnait.
    //
    // On peint donc les colonnes une fois les points de données validés, quand `graphics` existe.
    const paintColumns = () => {
      series.dataItems.forEach(dataItem => {
        const category =
          orientation === "vertical" ? dataItem.get("categoryX") : dataItem.get("categoryY")
        const mapped = resolveCategoryColor(category, colorMapper)
        if (!mapped) return

        const graphics = dataItem.get("graphics")
        if (!graphics) return

        graphics.setAll({ fill: am5.color(mapped), stroke: am5.color(mapped) })
      })
    }

    series.events.on("datavalidated", paintColumns)
    // Les données peuvent déjà être posées si la série est reconstruite : ne pas attendre l'évènement.
    paintColumns()

    // ⚠️ `paintColumns` seul ne suffit pas : amCharts crée les colonnes PARESSEUSEMENT, au rendu
    // (`BaseColumnSeries._prepareChildren` → `_createGraphics`), donc APRÈS `datavalidated`. Les
    // deux passages ci-dessus trouvaient des points sans `graphics` et ne peignaient rien — sans
    // erreur ; constaté le 06/10/2026 sur « Avancement moyen par priorité » (CRC). On peint donc
    // aussi chaque colonne au moment exact où elle reçoit son point de données (`_setDataItem`
    // émet `dataitemchanged`), comme le clic de drill-down, posé lui aussi sur le modèle.
    series.columns.template.events.on("dataitemchanged", ev => {
      const dataItem = ev.newDataItem as am5.DataItem<am5xy.IColumnSeriesDataItem> | undefined
      if (!dataItem) return
      const category =
        orientation === "vertical" ? dataItem.get("categoryX") : dataItem.get("categoryY")
      const mapped = resolveCategoryColor(category, colorMapper)
      if (mapped) ev.target.setAll({ fill: am5.color(mapped), stroke: am5.color(mapped) })
    })
  }

  const addSerieToChart = (
    chart: am5xy.XYChart,
    root: am5.Root,
    categoryField: string,
    data: any[]
  ) => {
    for (let i = 0; i < valueData.length; i++) {
      const nameToUse = i < dataNames.length ? dataNames[i] : valueData[i]
      const paletteColor = i < colorSetTheme.length ? colorSetTheme[i] : colorSetTheme[0]
      // Une regle valeur -> couleur sur le nom de serie l'emporte sur la palette indexee.
      const colorIndex = resolveMappedColor(nameToUse, colorMapper) ?? paletteColor

      let series: am5xy.ColumnSeries

      if (barOrientation === "vertical") {
        series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: nameToUse,
            xAxis: chart.xAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRenderer>,
            yAxis: chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRenderer>,
            valueYField: valueData[i],
            categoryXField: categoryField,
            stacked: typeBarChart === "stackedBar",
            ...(isStackedBar100 && { valueYShow: "valueYTotalPercent" }),
            fill: am5.color(colorIndex),
            stroke: am5.color(colorIndex),
          })
        )

        series.columns.template.setAll({
          width: am5.percent(columnsWidthPercent),
          cornerRadiusTL: columnCornerRadiusTopLeft,
          cornerRadiusTR: columnCornerRadiusTopRight,
          cornerRadiusBL: columnCornerRadiusBottomLeft,
          cornerRadiusBR: columnCornerRadiusBottomRight,
          strokeOpacity: 0,
        })

        applyCategoryColorMapper(series, "vertical")

        // Drill-down: click on bar
        if (onSegmentClick) {
          const seriesIdx = i
          series.columns.template.events.on("click", ev => {
            const dataItem = ev.target.dataItem as any
            if (!dataItem) return
            const categoryValue = dataItem.get("categoryX")
            if (categoryValue !== undefined) {
              onSegmentClick({ categoryValue: String(categoryValue), seriesIndex: seriesIdx })
            }
          })
        }

        // Labels
        if (!disableLabel) {
          series.bullets.push(() => {
            // Dehors : au-dessus de la barre, en couleur de texte. Dedans : centrée, en blanc.
            const dehors = labelColumnPosition === "outside"
            const label = am5.Label.new(root, {
              text: isStackedBar100 ? "{valueYTotalPercent.formatNumber('#.#')}%" : "{valueY}",
              fill: am5.color(dehors ? colors.textPrimary : "#fff"),
              centerX: am5.p50,
              centerY: dehors ? am5.p100 : am5.p50,
              // Dehors, l'étiquette se pose sur le fond de la carte, au milieu des libellés d'axe
              // et de la grille : le gras est ce qui la fait lire comme une DONNÉE. Dedans, elle
              // est déjà blanche sur un aplat coloré — le gras n'y ajouterait que du bruit.
              //
              // ⚠️ `"bold"` et NON `"600"` : la police du produit est Roboto, qui n'a pas de graisse
              // 600 (100/300/400/500/700/900). `_variables.scss` la demande pourtant à Google Fonts,
              // qui ne renvoie rien pour elle. amCharts dessine sur un CANVAS, où la substitution de
              // graisse est bien moins indulgente qu'en CSS : le texte retombait en maigre, sans
              // erreur. `bold` vise le 700, réellement chargé.
              ...(dehors ? { dy: -4, fontWeight: "bold" } : {}),
              fontSize: fontSizeLabelColumn,
              rotation: rotationLabelColumn,
              populateText: true,
            })

            // Adapter to hide labels for small percentages
            // Adapter for label text
            if (hideLabelIfPercentTooSmall || forceDisplayPercentSymbol) {
              label.adapters.add("text", (text, target) => {
                const dataItem = target.dataItem as any
                if (!dataItem) return text

                const value = isStackedBar100
                  ? dataItem.get("valueYTotalPercent")
                  : dataItem.get("valueY")

                if (
                  hideLabelIfPercentTooSmall &&
                  value !== undefined &&
                  value < valuePercentUnderWhichHideLabel
                ) {
                  return ""
                }

                if (forceDisplayPercentSymbol && !isStackedBar100) {
                  return value !== undefined ? `${value}%` : text
                }

                return text
              })
            }

            return am5.Bullet.new(root, {
              locationY: dehors ? 1 : 0.5,
              sprite: label,
            })
          })
        }

        // Total de la pile, au-dessus de la colonne
        if (displayStackTotal) {
          series.bullets.push(() => {
            const totalLabel = am5.Label.new(root, {
              text: "{valueYTotal}",
              fill: am5.color(stackTotalColor),
              centerX: am5.p50,
              centerY: am5.p100,
              dy: -4,
              fontSize: stackTotalFontSize,
              fontWeight: "600",
              populateText: true,
            })

            totalLabel.adapters.add("text", (text, target) =>
              isTopVisibleStackSeries(chart, i, target.dataItem) ? text : ""
            )

            return am5.Bullet.new(root, {
              locationY: 1,
              sprite: totalLabel,
            })
          })
        }

        // Tooltip
        if (tooltipActivation) {
          const tooltipBefore = i < textTooltipBefore.length ? textTooltipBefore[i] : nameToUse
          const tooltipAfter = i < textTooltipAfter.length ? textTooltipAfter[i] : ""

          let tooltipText = ""
          if (tooltipValueType === "text_and_value") {
            tooltipText = `${tooltipBefore}: [bold]{valueY}[/] ${tooltipAfter}`
          } else if (tooltipValueType === "value") {
            tooltipText = `[bold]{valueY}[/] ${tooltipAfter}`
          } else {
            tooltipText = `${tooltipBefore} ${tooltipAfter}`
          }

          series.columns.template.setAll({
            tooltipText: tooltipText.trim(),
          })
        }
      } else {
        // Horizontal
        series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: nameToUse,
            xAxis: chart.xAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRenderer>,
            yAxis: chart.yAxes.getIndex(0) as am5xy.CategoryAxis<am5xy.AxisRenderer>,
            valueXField: valueData[i],
            categoryYField: categoryField,
            stacked: typeBarChart === "stackedBar",
            ...(isStackedBar100 && { valueXShow: "valueXTotalPercent" }),
            fill: am5.color(colorIndex),
            stroke: am5.color(colorIndex),
          })
        )

        series.columns.template.setAll({
          height: am5.percent(columnsWidthPercent),
          cornerRadiusTL: columnCornerRadiusTopLeft,
          cornerRadiusTR: columnCornerRadiusTopRight,
          cornerRadiusBL: columnCornerRadiusBottomLeft,
          cornerRadiusBR: columnCornerRadiusBottomRight,
          strokeOpacity: 0,
        })

        applyCategoryColorMapper(series, "horizontal")

        // Drill-down: click on bar
        if (onSegmentClick) {
          const seriesIdx = i
          series.columns.template.events.on("click", ev => {
            const dataItem = ev.target.dataItem as any
            if (!dataItem) return
            const categoryValue = dataItem.get("categoryY")
            if (categoryValue !== undefined) {
              onSegmentClick({ categoryValue: String(categoryValue), seriesIndex: seriesIdx })
            }
          })
        }

        // Labels
        if (!disableLabel) {
          series.bullets.push(() => {
            // Dehors : à droite du bout de la barre, en couleur de texte. Dedans : centrée, blanche.
            const dehors = labelColumnPosition === "outside"
            const label = am5.Label.new(root, {
              text: isStackedBar100 ? "{valueXTotalPercent.formatNumber('#.#')}%" : "{valueX}",
              fill: am5.color(dehors ? colors.textPrimary : "#fff"),
              centerX: dehors ? am5.p0 : am5.p50,
              centerY: am5.p50,
              ...(dehors ? { dx: 4, fontWeight: "bold" } : {}),
              fontSize: fontSizeLabelColumn,
              rotation: rotationLabelColumn,
              populateText: true,
            })

            // Adapter to hide labels for small percentages
            // Adapter for label text
            if (hideLabelIfPercentTooSmall || forceDisplayPercentSymbol) {
              label.adapters.add("text", (text, target) => {
                const dataItem = target.dataItem as any
                if (!dataItem) return text

                const value = isStackedBar100
                  ? dataItem.get("valueXTotalPercent")
                  : dataItem.get("valueX")

                if (
                  hideLabelIfPercentTooSmall &&
                  value !== undefined &&
                  value < valuePercentUnderWhichHideLabel
                ) {
                  return ""
                }

                if (forceDisplayPercentSymbol && !isStackedBar100) {
                  return value !== undefined ? `${value}%` : text
                }

                return text
              })
            }

            // Adaptive color based on bar width (amCharts 5 doesn't have realWidth, using approximation)
            //
            // ⚠️ DEDANS UNIQUEMENT. Cet adaptateur bascule entre blanc et noir selon que la barre
            // est assez longue pour porter son étiquette. Dehors, l'étiquette est sur le fond de la
            // carte : il écraserait la couleur de texte du thème par un blanc invisible.
            if (!dehors)
            label.adapters.add("fill", (fill, target) => {
              const dataItem = target.dataItem as any
              if (!dataItem) return fill

              const value = isStackedBar100
                ? dataItem.get("valueXTotalPercent")
                : dataItem.get("valueX")

              // If value is very small, use black for better visibility
              if (value !== undefined && value < 10) {
                return am5.color("#000")
              }

              return am5.color("#fff")
            })

            return am5.Bullet.new(root, {
              locationX: dehors ? 1 : 0.5,
              sprite: label,
            })
          })
        }

        // Total de la pile, a droite de la barre
        if (displayStackTotal) {
          series.bullets.push(() => {
            const totalLabel = am5.Label.new(root, {
              text: "{valueXTotal}",
              fill: am5.color(stackTotalColor),
              centerX: am5.p0,
              centerY: am5.p50,
              dx: 4,
              fontSize: stackTotalFontSize,
              fontWeight: "600",
              populateText: true,
            })

            totalLabel.adapters.add("text", (text, target) =>
              isTopVisibleStackSeries(chart, i, target.dataItem) ? text : ""
            )

            return am5.Bullet.new(root, {
              locationX: 1,
              sprite: totalLabel,
            })
          })
        }

        // Tooltip
        if (tooltipActivation) {
          const tooltipBefore = i < textTooltipBefore.length ? textTooltipBefore[i] : nameToUse
          const tooltipAfter = i < textTooltipAfter.length ? textTooltipAfter[i] : ""

          let tooltipText = ""
          if (tooltipValueType === "text_and_value") {
            tooltipText = `${tooltipBefore}: [bold]{valueX}[/] ${tooltipAfter}`
          } else if (tooltipValueType === "value") {
            tooltipText = `[bold]{valueX}[/] ${tooltipAfter}`
          } else {
            tooltipText = `${tooltipBefore} ${tooltipAfter}`
          }

          series.columns.template.setAll({
            tooltipText: tooltipText.trim(),
          })
        }
      }

      series.data.setAll(data)
    }
  }

  // Effect for initial chart creation only
  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
        rootRef.current = null
        chartRef.current = null
      }
    }
  }, [])

  // Effect for updates
  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Bar chart handling
      let processedData = prepareChartData(filteredDataChart)
      const categoryField = Array.isArray(categoryData) ? "combinedCategory" : categoryData
      const dataSort = trierCategories(processedData, categoryField)

      // Clean chart (removes legends, etc.)
      cleanChart(chart)
      clearLegends(chart)

      // Reapply chart configurations
      setupChart(chart, root)

      // Clear and rebuild axes and series
      chart.xAxes.clear()
      chart.yAxes.clear()
      chart.series.clear()

      // Rebuild axes and series with current configuration
      rebuildChartStructure(chart, root, categoryField, dataSort)
    }
  })

  return <div id={ID} style={{ width, height }} />
}

export default BarChart
